import { useMemo } from 'react';

import { SCALE_DAILY, START_DAY_MON } from '../ComposedChart/constants';
import { getDateByPeriodIndex, getPeriodDateIndex } from '../utils';

export const useMergedSeriesData = (
  series = [],
  scale = SCALE_DAILY,
  scaleWeekStart = START_DAY_MON
) => {
  const dateOffset = useMemo(() => {
    const startIndexes = series
      .filter(({ data }) => data.length)
      .map(({ data, period, dateKey }) => {
        const firstDate = data && data[0] && data[0][dateKey];
        return getPeriodDateIndex(
          period[0],
          0,
          scale,
          scaleWeekStart
        )(firstDate);
      });
    return startIndexes.length ? Math.min(...startIndexes) : 0;
  }, [series]);

  const mergedData = useMemo(() => {
    let maxIndex = -1;

    const mergedRowsWithoutNull = series.reduce(
      (memoRows, { data = [], period, dateKey }) => {
        if (!data.length) {
          return memoRows;
        }
        const getDateIndex = getPeriodDateIndex(
          period[0],
          dateOffset,
          scale,
          scaleWeekStart
        );

        data.forEach((row) => {
          const rowIndex = getDateIndex(row[dateKey]);
          maxIndex = Math.max(maxIndex, rowIndex);
          memoRows[rowIndex] = {
            category: rowIndex,
            ...row,
            ...memoRows[rowIndex],
          };
        });

        return memoRows;
      },
      []
    );

    return Array(maxIndex + 1)
      .fill(null)
      .map((_, i) => {
        const row = mergedRowsWithoutNull[i];
        return row ? row : { category: i, filled: false };
      });
  }, [series]);

  return {
    data: mergedData,
    getCategoryPeriod: (category) => {
      return series.map(({ period }) =>
        getDateByPeriodIndex(
          period[0],
          dateOffset,
          scale,
          scaleWeekStart
        )(category)
      );
    },
    getBasePeriodDateCategory: (date) => {
      const basePeriod = series[0].period;
      return getPeriodDateIndex(
        basePeriod[0],
        dateOffset,
        scale,
        scaleWeekStart
      )(date);
    },
  };
};
